// @ts-strict

import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ICountry, IDeviceType, ISingleCampaign } from '@app/core/models/core-models';
import { AvailableLanguages } from '@app/core/services/available-languages/available-languages.service.types';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { CAMPAIGN_PREVIEW_VIEW_TYPE, CampaignPreviewService } from './campaign-preview.service';
import { ShareOpenModalStatusService } from '@services/share-open-modal-status/share-open-modal-status.service';
import { SingleCampaignModalComponent } from '@app/shared/components/modals/single-campaign-modal.component';
import { GlobalNotificationsService } from '@app/core/services/global-notifications/global-notifications.service';
import { NotificationType, NotificationTypes } from '@app/core/services/global-notifications/global-notifications.model';
import { CONTENT_LOCKER } from '@app/core/helpers/route.constants';

enum AvailableDestinations {
  cpa_locker = 'cpa-locker',
  captcha_locker = 'captcha-locker',
  file_locker = 'file-locker',
  mobile_rewards = 'mobile-rewards'
}

@Component({
  selector: 'app-campaign-preview-modal',
  templateUrl: './campaign-preview-modal.component.html',
  styleUrls: ['./campaign-preview-modal.component.scss']
})
export class CampaignPreviewModalComponent implements OnInit, OnDestroy {
  constructor(
    private readonly activeModal: NgbActiveModal,
    private readonly campaignPreviewService: CampaignPreviewService,
    private readonly globalNotificationsService: GlobalNotificationsService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly shareOpenModalStatus: ShareOpenModalStatusService,
    private readonly modalService: NgbModal
  ) {}

  @Input() viewType?: CAMPAIGN_PREVIEW_VIEW_TYPE;
  @Input() campaignData?: ISingleCampaign;
  @Input() modalDescriptionName: string = '';
  @Input() modalTitle: string = '';
  @Input() modalType: string = '';
  @Input() smartlinks = false;
  @Input() onCountryClick?: (isActive: boolean, countryId?: number) => void;

  public campaignsViewType = CAMPAIGN_PREVIEW_VIEW_TYPE.campaigns;
  public fullCampaign: ISingleCampaign | null = null;
  public isCreatingLocker = false;
  public flattenedDeviceTypes: number[] = [];
  public isLoading: boolean = false;
  public isError: boolean = false;
  public openedModalRef?: NgbModalRef;
  public description = '';
  public currentLanguage: AvailableLanguages = 'en';

  private destroy$ = new Subject<boolean>();

  public get availableDestinations(): typeof AvailableDestinations {
    return AvailableDestinations;
  }

  ngOnInit(): void {
    this.currentLanguage = this.translate.currentLang as AvailableLanguages;
    this.campaignPreviewService.isLoading$
      .pipe(
        tap(isLoading => (this.isLoading = isLoading)),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.campaignPreviewService.error$
      .pipe(
        tap(error => (this.isError = error)),
        tap(error => error && this.showNotification('view_translations.error_occured', NotificationTypes[1])),
        tap(error => (error ? this.onClose() : null)),
        takeUntil(this.destroy$)
      )
      .subscribe();

    if (this.campaignData?.id && this.viewType) {
      this.campaignPreviewService
        .getSingleCampaign(this.campaignData.id, this.viewType)
        .pipe(
          tap(campaign => {
            this.fullCampaign = { ...campaign, countries: this.campaignData?.countries };
            this.fullCampaign!.device_types!.map(deviceType => this.flattenDeviceTypes(deviceType));
            this.description = this.setDescription(this.fullCampaign!);
          }),
          takeUntil(this.destroy$)
        )
        .subscribe();
    }

    this.translate.onLangChange
      .pipe(
        tap(lang => {
          this.currentLanguage = lang.lang as AvailableLanguages;
          this.description = this.setDescription(this.fullCampaign!);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public onClose(): void {
    this.activeModal.close();
  }

  public onCreateLocker = () => {
    if (!this.fullCampaign?.access) return;

    this.isCreatingLocker = true;
  };

  public onLockerClick = (destination: AvailableDestinations) => {
    const suffix = 'create';

    this.router.navigate([CONTENT_LOCKER, destination, suffix]);

    return this.onClose();
  };

  private flattenDeviceTypes(deviceType: IDeviceType) {
    this.flattenedDeviceTypes.push(deviceType.id || 0);
    if (deviceType.children?.length) {
      deviceType.children.map(subcat => {
        this.flattenDeviceTypes(subcat);
      });
    } else {
      return;
    }
  }

  private showNotification(message: string, type: NotificationType): void {
    this.globalNotificationsService.show({
      message,
      type,
      translate: true,
      isPersistent: false,
      id: ''
    });
  }

  private setDescription = (campaign: ISingleCampaign): string => {
    let description = '';

    const availableDescriptions: Record<AvailableLanguages, string> = Object.assign({}, ...(campaign.description ?? []));

    description = availableDescriptions[this.currentLanguage];

    return description;
  };

  public openCountriesModal = () => {
    this.shareOpenModalStatus.shareModalStatus(true);
    const countrySelectInactive = this.router.url.includes('incent-campaigns');
    const modalRef = this.modalService.open(SingleCampaignModalComponent, {
      size: 'me',
      backdrop: 'static'
    });
    this.openedModalRef = modalRef;
    const modalProps = {
      campaignData: { ...this.fullCampaign, countries: this.fullCampaign?.countries },
      modalTitle: 'view_translations.countries',
      modalType: 'countries',
      modalDescriptionName: '',
      watchInactiveCountries: !countrySelectInactive,
      onCountryClick: countrySelectInactive ? undefined : this.updateDecision
    };

    modalRef.componentInstance.onCountryClick = modalProps.onCountryClick;
    modalRef.componentInstance.watchInactiveCountries = modalProps.watchInactiveCountries;
    modalRef.componentInstance.campaignData = modalProps.campaignData;
    modalRef.componentInstance.modalTitle = modalProps.modalTitle;
    modalRef.componentInstance.modalType = modalProps.modalType;
    modalRef.componentInstance.modalDescriptionName = modalProps.modalDescriptionName;
  };

  public trackCountry = (_index: number, country: ICountry) => {
    return country.id;
  };

  public updateDecision = (isActive: boolean, countryId: number) => {
    if (typeof this.onCountryClick === 'function') {
      return this.onCountryClick(isActive, countryId);
    }
  };

  public handleCountry = (countryId: number, isActive: boolean) => {
    const updatedCountries = this.fullCampaign?.countries?.map(country => (country.id === countryId ? { ...country, active: isActive } : country));
    this.fullCampaign = { ...(this.fullCampaign || {}), countries: updatedCountries || [] };
  };
}
